import { CheckCircleTwoTone, MessageTwoTone, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { communicationSkillsChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'How do I actually listen instead of just waiting for my turn to talk?',
    answer: (
      <>
        Focus completely on what the other person is saying instead of planning your response. If your mind wanders, gently bring your
        attention back to their words. This kind of focus is the same skill used in{' '}
        <Link to="/adulting-101/time-management">Time Management</Link> when you limit distractions to concentrate on one thing.
      </>
    ),
  },
  {
    question: 'Why does eye contact matter so much?',
    answer: (
      <>
        Eye contact shows the other person you're present and engaged, which builds trust. Too little makes you seem uninterested or
        nervous, while too much can feel intense — aim for natural, comfortable contact. Reading how others react to you is part of{' '}
        <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link>.
      </>
    ),
  },
  {
    question: 'How do I know if someone is actually listening to me?',
    answer: (
      <>
        They make eye contact, nod, say things like "I see" or "that makes sense," and ask follow-up questions. If someone is on their phone
        or giving one-word answers, they're probably not fully engaged. Noticing this helps you figure out who your real supporters are in{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link>.
      </>
    ),
  },
  {
    question: 'Why should I ask follow-up questions instead of just assuming?',
    answer: (
      <>
        Assuming you know what someone means often leads to misunderstandings. Asking a clarifying question shows you care about getting it
        right. This same habit of digging into the real issue is central to good{' '}
        <Link to="/adulting-101/problem-solving">Problem Solving</Link>.
      </>
    ),
  },
  {
    question: 'Does remembering small details about someone actually matter?',
    answer: (
      <>
        Yes — bringing up something they told you last week shows you were truly paying attention, not just being polite. It's one of the
        easiest ways to make people feel valued, which strengthens your{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link> relationships.
      </>
    ),
  },
  {
    question: "Should I apologize if I'm not sure if I did something wrong?",
    answer: (
      <>
        It's usually better to ask directly rather than assume. You could say, "I want to make sure I didn't upset you — did I say something
        wrong?" Being willing to check in and own your part is part of building{' '}
        <Link to="/adulting-101/responsibility">Responsibility</Link>.
      </>
    ),
  },
  {
    question: 'How do I stay respectful when I disagree with someone?',
    answer: (
      <>
        Use "I" statements instead of "you always...", acknowledge what they said even if you disagree, and avoid raising your voice.
        Disagreeing doesn't mean the relationship is over. Managing your reaction in the moment is a core part of{' '}
        <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link>.
      </>
    ),
  },
  {
    question: 'What does my body language say when I talk to people?',
    answer: (
      <>
        Crossed arms, looking away, or fidgeting can signal that you're closed off or uninterested, even if you don't mean it that way.
        Open, relaxed posture invites better conversation. This kind of self-awareness carries over into{' '}
        <Link to="/adulting-101/like-love-lust">Like, Love, Lust</Link>, where reading body language helps you understand how someone really
        feels.
      </>
    ),
  },
]

export function Adulting101CommunicationSkillsPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Communication Skills</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #e6f7ff 0%, #ffffff 220px)' }}>
        <Title level={2}>Good communication skills help you build better relationships and get what you need.</Title>
        <Paragraph type="secondary">
          Do people seem to avoid talking to you? Learning to listen, speak clearly, and show respect can transform how others see you.
        </Paragraph>

        <Card
          title={
            <Space>
              <MessageTwoTone />
              Communication Skills Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you communicated like a respectful adult!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Communication is something you practice every day. The more mindful you are about how you talk to others, the better you\'ll
              get.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Communication Skills
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
